import { Component } from '@theme/component';
import { debounce, yieldToMainThread } from '@theme/utilities';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class ADHeaderMenu extends Component {
    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();
        this.#bindSharedListeners();
    }

    #handleResize = debounce(() => this.#syncSubmenuAlignment(), 100);

    #syncSubmenuAlignment() {
        const wrapper = this.closest('.na-header')?.querySelector('.header-menu-wrapper');
        const section = this.closest('.header-section');
        if (!wrapper || !section) return;

        const wrapperLeft = wrapper.getBoundingClientRect().left - section.getBoundingClientRect().left;
        section.style.setProperty('--submenu-align-start', `${wrapperLeft}px`);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    updatedCallback() {
        this.#abortController.abort();
        this.#abortController = new AbortController();
        this.#bindSharedListeners();
    }

    #bindSharedListeners() {
        const { signal } = this.#abortController;

        this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), { signal });
        this.addEventListener('pointerleave', () => this.#debouncedDeactivate(), { signal });
        this.#syncSubmenuAlignment();
        window.addEventListener('resize', this.#handleResize, { signal, passive: true });
        document.addEventListener('keydown', this.#onDocumentKeyDown, { signal });
    }

    /**
     * @param {KeyboardEvent} event
     */
    #onDocumentKeyDown = (event) => {
        if (event.key !== 'Escape') return;

        const item = this.#state.activeItem;
        if (!item) return;

        event.preventDefault();
        event.stopPropagation();
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate.cancel();
        this.#deactivate(item, { force: true });
        item.focus();
    };

    /**
     * @type {State}
     */
    #state = {
        activeItem: null,
    };

    /**
     * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
     * @returns {number}
     */
    get animationDelay() {
        const value = this.dataset.animationDelay;
        return value ? parseInt(value, 10) : 0;
    }

    /**
     * Get the overflow menu element
     */
    get overflowMenu() {
        return this.refs.overflowMenu;
    }

    /**
     * Get the overflow menu part from shadow DOM
     */
    get overflowMenuPart() {
        return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
    }

    /**
     * Whether the overflow menu is hovered
     * @returns {boolean}
     */
    get overflowHovered() {
        return this.refs.overflowMenu?.matches(':hover') ?? false;
    }

    /**
     * Whether the active submenu is hovered
     * @returns {boolean}
     */
    get submenuHovered() {
        if (!this.#state.activeItem) return false;
        const submenu = findSubmenu(this.#state.activeItem);
        return submenu?.matches(':hover') ?? false;
    }

    /**
     * Activate the selected menu item immediately
     * @param {PointerEvent | FocusEvent} event
     */
    activate = (event) => {
        this.#debouncedDeactivate.cancel();
        this.#debouncedActivateHandler.cancel();

        this.#debouncedActivateHandler(event);
    };

    /**
     * Activate the selected menu item with a delay
     * @param {PointerEvent | FocusEvent} event
     */
    #activateHandler = async (event) => {
        this.#debouncedDeactivate.cancel();

        this.removeAttribute('data-animating');

        if (!(event.target instanceof Element)) return;

        const item = findMenuItem(event.target);

        if (!item || item === this.#state.activeItem) return;

        const isDefaultSlot = event.target.slot === '';
        const previouslyActiveItem = this.#state.activeItem;
        const previouslyActiveListItem = previouslyActiveItem?.parentElement;
        const previousSubmenu = previouslyActiveItem ? findSubmenu(previouslyActiveItem) : null;
        const listItem = item.parentElement;
        let submenu = findSubmenu(item);
        if (!submenu && !isDefaultSlot) submenu = this.overflowMenuPart;

        await yieldToMainThread();

        if (previouslyActiveListItem) {
            previouslyActiveListItem.ariaExpanded = 'false';
            previouslyActiveListItem.removeAttribute('data-expanded');
            if (previousSubmenu) delete previousSubmenu.dataset.active;
        }

        this.dataset.overflowExpanded = (!isDefaultSlot).toString();
        this.#state.activeItem = item;
        this.ariaExpanded = 'true';
        item.ariaExpanded = 'true';
        if (listItem) {
            listItem.ariaExpanded = 'true';
            listItem.dataset.expanded = 'true';
        }

        if (!isDefaultSlot && this.overflowMenu) {
            this.overflowMenu.dataset.expanded = 'true';
        }

        if (submenu) submenu.dataset.active = '';

        const submenuHeight = submenu ? submenu.offsetHeight : 0;
        this.style.setProperty('--submenu-height', `${submenuHeight}px`);
        this.style.setProperty('--submenu-opacity', '1');
    };

    #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

    /**
     * Deactivate the active item after a delay
     * @param {PointerEvent | FocusEvent} event
     */
    deactivate(event) {
        this.#debouncedActivateHandler.cancel();

        if (!(event.target instanceof Element)) return;

        const item = findMenuItem(event.target);

        // Make sure the item to be deactivated is still the active one. Ideally
        // we cancelled the debounce before the item was changed, but just in case.
        if (item === this.#state.activeItem) {
            this.#debouncedDeactivate();
        }
    }

    /**
     * Deactivate the active item immediately
     * @param {HTMLElement | null} [item]
     * @param {{ force?: boolean }} [options]
     */
    #deactivate = (item = this.#state.activeItem, options = {}) => {
        if (!item || item !== this.#state.activeItem) return;
        if (!options.force && (this.overflowHovered || this.submenuHovered)) return;

        const listItem = item.parentElement;

        this.style.setProperty('--submenu-height', '0px');
        this.style.setProperty('--submenu-opacity', '0');
        this.dataset.overflowExpanded = 'false';

        // Remove data-expanded from overflow-list
        if (this.overflowMenu) {
            this.overflowMenu.removeAttribute('data-expanded');
        }

        const submenu = findSubmenu(item);

        this.#state.activeItem = null;
        this.ariaExpanded = 'false';
        item.ariaExpanded = 'false';
        listItem.ariaExpanded = 'false';
        listItem.removeAttribute('data-expanded');
        listItem.setAttribute('data-animating', '');

        setTimeout(
            () => {
                listItem.removeAttribute('data-animating');
                // Remove active state from submenu after animation completes
                if (submenu) {
                    delete submenu.dataset.active;
                }
            },
            Math.max(0, this.animationDelay - 150)
        );
    };

    #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);
}

if (!customElements.get('header-menu-component')) {
    customElements.define('header-menu-component', ADHeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
    if (!(element instanceof Element)) return null;

    if (element?.matches('[slot="more"]')) {
        // Select the first overflowing menu item when hovering over the "More" item
        return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
    }

    return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
    if (!element) return null;

    // First try direct child of the menu item's parent (li element)
    const menuItem = element.closest('[ref="menuitem"]')?.parentElement;
    if (menuItem) {
        const submenu = menuItem.querySelector(':scope > [ref="submenu[]"]');
        if (submenu instanceof HTMLElement) return submenu;
    }

    // Fallback: try the original approach
    const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
    return submenu instanceof HTMLElement ? submenu : null;
}
